// Э-10. Отчёты: период, завод, клиент, группировка; выгрузка в Excel.

import { Fragment, useState } from "react";
import { addDays, fmtNum, localISODate } from "../../shared/format";
import { TopBar } from "../../shared/shell";
import { Button, Field, Input, Loading, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { api, useCtx } from "../ctx";

type Kind = "shipments" | "orders" | "changes" | "ontime" | "planfact" | "site-idle";
const GROUPS: Record<string, { value: string; label: string }[]> = {
  shipments: [{ value: "day", label: "по дням" }, { value: "plant", label: "по заводам" }, { value: "customer", label: "по клиентам" }, { value: "site", label: "по объектам" }, { value: "grade", label: "по маркам" }],
  ontime: [{ value: "plant", label: "по заводам" }, { value: "customer", label: "по клиентам" }, { value: "day", label: "по дням" }],
  planfact: [{ value: "customer", label: "по клиентам" }, { value: "site", label: "по объектам" }, { value: "plant", label: "по заводам" }, { value: "grade", label: "по маркам" }],
};

export function ReportsPage() {
  const { refs } = useCtx();
  const toast = useToast();
  const today = localISODate();
  const [kind, setKind] = useState<Kind>("shipments");
  const [start, setStart] = useState(addDays(today, -30));
  const [end, setEnd] = useState(today);
  const [plant, setPlant] = useState("");
  const [group, setGroup] = useState("day");
  const [gran, setGran] = useState("month");
  const r = useAsync<any>(() => kind === "planfact"
    ? api.get("/supply-plans/planfact", { start, end, group_by: group, granularity: gran })
    : api.get(`/reports/${kind}`, { start, end, plant: plant || undefined, group_by: GROUPS[kind] ? group : undefined }), [kind, start, end, plant, group, gran]);
  const changeKind = (k: Kind) => { setKind(k); setGroup(GROUPS[k]?.[0].value || ""); };
  return (
    <>
      <TopBar title="Отчёты">
        {kind === "planfact" && (
          <Button onClick={() => api.open("/supply-plans/planfact/export.xlsx", { start, end, group_by: group, granularity: gran }, `planfact_${start}_${end}.xlsx`).catch((e) => toast.error(e))}>
            Выгрузить в Excel
          </Button>
        )}
        {kind !== "planfact" && (
          <Button onClick={() => api.open(`/reports/${kind}/export.xlsx`, { start, end, plant: plant || undefined, group_by: GROUPS[kind] ? group : undefined }, `${kind}_${start}_${end}.xlsx`).catch((e) => toast.error(e))}>
            Выгрузить в Excel
          </Button>
        )}
      </TopBar>
      <div className="content">
        <Tabs value={kind} onChange={changeKind} items={[
          { value: "shipments", label: "Отгрузки" }, { value: "orders", label: "Выполнение заявок" }, { value: "changes", label: "Переносы и отмены" },
          { value: "ontime", label: "Соблюдение сроков" }, { value: "planfact", label: "План/факт" }, { value: "site-idle", label: "Простой на объектах" }]} />
        <div className="row" style={{ alignItems: "flex-end" }}>
          <Field label="С"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="По"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          {kind !== "planfact" && kind !== "site-idle" && <Field label="Завод"><Select value={plant} onChange={(e) => setPlant(e.target.value)} placeholder="Все" options={refs.plants.map((p) => ({ value: p.id, label: p.name }))} /></Field>}
          {GROUPS[kind] && <Field label="Группировка"><Select value={group} onChange={(e) => setGroup(e.target.value)} options={GROUPS[kind]} /></Field>}
          {kind === "planfact" && <Field label="Шаг"><Select value={gran} onChange={(e) => setGran(e.target.value)} options={[{ value: "day", label: "день" }, { value: "week", label: "неделя" }, { value: "month", label: "месяц" }, { value: "year", label: "год" }]} /></Field>}
        </div>
        {!r.data ? <Loading error={r.error} onRetry={r.reload} /> : <ReportTable kind={kind} data={r.data} />}
      </div>
    </>
  );
}

function ReportTable({ kind, data }: { kind: Kind; data: any }) {
  // пока грузится новый отчёт, на руках данные прежнего — другой формы
  if (kind === "site-idle") return data.details ? <IdleReport data={data} /> : null;
  if (kind === "planfact" ? !data.buckets : !data.columns) return null;
  if (kind === "planfact") return <PlanFact data={data} />;
  const rows: any[] = data.rows;
  return (
    <div className="stack">
      {kind === "ontime" && data.total && (
        <div className="kpis">
          <div className="kpi"><div className="k">Рейсов</div><div className="v">{data.total.count}</div></div>
          <div className="kpi"><div className="k">В допуске</div><div className="v">{data.total.on_time_pct === null ? "—" : fmtNum(data.total.on_time_pct, 0) + "%"}</div></div>
          <div className="kpi"><div className="k">Среднее опоздание</div><div className="v">{data.total.avg_delay_min ?? "—"} мин</div></div>
        </div>
      )}
      {(data.by_reason || data.delay_reasons) && (
        <div className="row">{(data.by_reason || data.delay_reasons).map((r: any) => <span key={r.reason} className="tag plain">{r.reason}: <b>{r.count}</b></span>)}</div>
      )}
      <div className="panel table-wrap">
        <table className="table">
          <thead><tr>{data.columns.map((c: string) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i}>{rowCells(kind, r).map((c, j) => <td key={j} className={typeof c === "number" ? "num" : ""}>{typeof c === "number" ? fmtNum(c) : c}</td>)}</tr>)}</tbody>
        </table>
        {rows.length === 0 && <p className="empty">За период данных нет.</p>}
      </div>
    </div>
  );
}

function rowCells(kind: Kind, r: any): (string | number | null)[] {
  if (kind === "shipments") return [r.group, r.trips, r.volume_m3];
  if (kind === "orders") return [r.number, r.date, r.customer, r.site, r.grade, r.ordered_m3, r.shipped_m3, r.pct, r.status, r.channel, r.short_notice ? "да" : ""];
  if (kind === "changes") return [r.at, r.number, r.customer, r.site, r.volume_m3, r.action, r.reason, r.user];
  return [r.group, r.count, r.on_time, r.on_time_pct, r.avg_delay_min];
}

/** ФТ-9.6: простой миксеров на объектах сверх нормы (плановая разгрузка + допуск). */
function IdleReport({ data }: { data: any }) {
  return (
    <div className="stack">
      <div className="kpis">
        <div className="kpi"><div className="k">Простой сверх нормы</div><div className="v">{fmtNum(data.total_idle_minutes / 60, 1)} ч</div></div>
        <div className="kpi"><div className="k">Рейсов с простоем</div><div className="v">{data.details.length}</div></div>
        <div className="kpi"><div className="k">Допуск</div><div className="v">{data.tolerance_minutes} мин</div></div>
      </div>
      <p className="small muted" style={{ margin: 0 }}>Норма — плановая разгрузка рейса плюс допуск (настройка «Допуск простоя на объекте»). Считается от прибытия на объект до окончания разгрузки.</p>
      <div className="panel table-wrap">
        <table className="table">
          <thead><tr><th>Объект</th><th>Клиент</th><th className="num">Рейсов</th><th className="num">С простоем</th><th className="num">Простой, мин</th><th className="num">Самый долгий, мин</th></tr></thead>
          <tbody>{data.rows.map((r: any) => (
            <tr key={r.site + r.customer}><td>{r.site}</td><td>{r.customer}</td><td className="num">{r.trips}</td><td className="num">{r.idle_trips}</td><td className="num">{r.idle_minutes}</td><td className="num">{r.max}</td></tr>
          ))}</tbody>
        </table>
        {data.rows.length === 0 && <p className="empty">За период рейсов на объектах нет.</p>}
      </div>
      {data.details.length > 0 && (
        <div className="panel table-wrap">
          <table className="table">
            <thead><tr><th>Дата</th><th>Заявка</th><th>Объект</th><th>Машина</th><th className="num">Простой, мин</th></tr></thead>
            <tbody>{data.details.slice(0, 200).map((d: any, i: number) => (
              <tr key={i}><td>{d.date}</td><td>№{d.order_number}, рейс {d.seq}</td><td>{d.site}</td><td>{d.plate || "—"}</td><td className="num">{d.idle_minutes}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** План/факт (замечание 15.10 №8): по периоду — план и факт отдельными столбцами, итог, отклонение, % с цветом. */
function PlanFact({ data }: { data: any }) {
  const wp: boolean = data.with_plan;
  const pctCls = (p: number | null) => (p == null ? "" : p >= 100 ? "pf-good" : p < 90 ? "pf-bad" : "pf-warn");
  const row = (r: any, total = false) => (
    <tr key={r.key} className={total ? "pf-total" : undefined}>
      <td>{r.name}</td>
      {r.cells.map((c: any) => (
        <Fragment key={c.bucket}>
          {wp && <td className="num muted">{fmtNum(c.plan, 0)}</td>}
          <td className="num">{fmtNum(c.fact, 0)}</td>
        </Fragment>
      ))}
      {wp && <td className="num">{fmtNum(r.plan, 0)}</td>}
      <td className="num">{fmtNum(r.fact, 0)}</td>
      {wp && <td className={`num ${r.diff < 0 ? "pf-bad" : ""}`}>{r.diff > 0 ? "+" : ""}{fmtNum(r.diff, 0)}</td>}
      {wp && <td className={`num ${pctCls(r.pct)}`}>{r.pct == null ? "—" : `${fmtNum(r.pct, 0)}%`}</td>}
    </tr>
  );
  return (
    <div className="panel table-wrap">
      {!wp && <p className="small muted pb" style={{ margin: 0 }}>План поставок ведётся по клиентам и объектам — в этой группировке показан только факт.</p>}
      <table className="table pf">
        <thead>
          <tr>
            <th rowSpan={wp ? 2 : 1}>Группа</th>
            {data.buckets.map((b: string) => <th key={b} className="num" colSpan={wp ? 2 : 1}>{data.labels?.[b] || b}</th>)}
            <th className="num" colSpan={wp ? 4 : 1}>За период, м³</th>
          </tr>
          {wp && (
            <tr>
              {data.buckets.map((b: string) => <Fragment key={b}><th className="num">план</th><th className="num">факт</th></Fragment>)}
              <th className="num">план</th><th className="num">факт</th><th className="num">отклонение</th><th className="num">%</th>
            </tr>
          )}
        </thead>
        <tbody>
          {data.rows.map((r: any) => row(r))}
          {data.rows.length > 1 && data.total && row(data.total, true)}
        </tbody>
      </table>
      {data.rows.length === 0 && <p className="empty">За период данных нет.</p>}
    </div>
  );
}
